import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PLAN_CATALOG } from './planConfig';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface OrganizationSettingsProps {
  user: any;
  token: string;
  onClose: () => void;
  onPlanChanged?: (newPlan: string) => void;
}

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({ user, token, onClose, onPlanChanged }) => {
  const { language } = useLanguage();
  const L = (language === 'en' ? enStrings : elStrings).orgSettings;
  const [orgName, setOrgName] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [keyPreview, setKeyPreview] = useState<string | null>(null);
  const [keySet, setKeySet] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [aiModel, setAiModel] = useState('gpt-4o');
  const [savedName, setSavedName] = useState('');
  const [savedModel, setSavedModel] = useState('gpt-4o');
  const [aiModelOptions, setAiModelOptions] = useState<{ openai: {id:string;name:string;description:string}[]; anthropic: {id:string;name:string;description:string}[] } | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [plan, setPlan] = useState('');
  const [nextPaymentDate, setNextPaymentDate] = useState<string | null>(null);
  const [projectCount, setProjectCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [maxProjects, setMaxProjects] = useState<number | null>(null);
  const [maxUsers, setMaxUsers] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [trialCheckoutLoading, setTrialCheckoutLoading] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [stripePrices, setStripePrices] = useState<Record<string, number | null>>({});
  const [dbPlanLimits, setDbPlanLimits] = useState<Record<string, { maxProjects: number | null; maxUsers: number | null }>>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    axios.get(`${API}/payments/prices`).then(r => setStripePrices(r.data)).catch(() => {});
    axios.get(`${API}/payments/plan-limits`).then(r => setDbPlanLimits(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/organizations/my/ai-models`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setAiModelOptions(res.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/organizations/my/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setOrgName(res.data.name || '');
      setSavedName((res.data.name || '').trim());
      setKeySet(res.data.openai_api_key_set || false);
      setKeyPreview(res.data.openai_api_key_preview || null);
      setAiModel(res.data.ai_model || 'gpt-4o');
      setSavedModel(res.data.ai_model || 'gpt-4o');
      setHasSubscription(res.data.has_subscription || false);
      setPlan(res.data.plan || '');
      setNextPaymentDate(res.data.next_payment_date || null);
      setProjectCount(res.data.project_count ?? 0);
      setUserCount(res.data.user_count ?? 0);
      setMaxProjects(res.data.max_projects ?? null);
      setMaxUsers(res.data.max_users ?? null);
      setCancelAtPeriodEnd(res.data.cancel_at_period_end || false);
      setTrialEndsAt(res.data.trial_ends_at || null);
    }).catch(() => {
      setError(L.errorLoadSettings);
    }).finally(() => setLoading(false));
  }, [token]);

  const detectProvider = (key: string) => key.startsWith('sk-ant-') ? 'anthropic' : 'openai';

  const defaultModelForProvider = (provider: 'openai' | 'anthropic') =>
    provider === 'anthropic'
      ? (aiModelOptions?.anthropic?.[1]?.id ?? 'claude-sonnet-4-6')
      : (aiModelOptions?.openai?.[0]?.id ?? 'gpt-4o');

  // Provider derived from key being typed, or from current saved model
  const activeProvider: 'openai' | 'anthropic' = openaiKey.startsWith('sk-')
    ? detectProvider(openaiKey)
    : (aiModel.startsWith('claude-') ? 'anthropic' : 'openai');

  const visibleModels = activeProvider === 'anthropic'
    ? (aiModelOptions?.anthropic ?? [{ id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', description: '' }])
    : (aiModelOptions?.openai ?? [{ id: 'gpt-4o', name: 'GPT-4o', description: '' }]);

  const handleKeyChange = (val: string) => {
    setOpenaiKey(val);
    setError('');
    // Auto-switch model when provider changes
    if (val.startsWith('sk-')) {
      const newProvider = detectProvider(val);
      const currentProvider = aiModel.startsWith('claude-') ? 'anthropic' : 'openai';
      if (newProvider !== currentProvider) {
        setAiModel(defaultModelForProvider(newProvider));
      }
    }
  };

  const isDirty = orgName.trim() !== savedName || aiModel !== savedModel || openaiKey.trim() !== '';

  // One save for name + AI settings — sends only the fields that changed
  const handleSave = async () => {
    const name = orgName.trim();
    const key = openaiKey.trim();
    if (!name) { setError(L.errorNameEmpty); return; }
    if (key && !key.startsWith('sk-')) { setError(L.errorKeyPrefix); return; }
    const updates: { name?: string; openai_api_key?: string; ai_model?: string } = {};
    if (name !== savedName) updates.name = name;
    if (aiModel !== savedModel) updates.ai_model = aiModel;
    // Key and model are always saved together so the model matches the key's provider
    if (key) { updates.openai_api_key = key; updates.ai_model = aiModel; }
    if (!Object.keys(updates).length) return;
    setError(''); setSuccess(''); setSaving(true);
    try {
      await axios.put(`${API}/organizations/my/settings`, updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedName(name);
      setOrgName(name);
      setSavedModel(aiModel);
      if (key) {
        setKeySet(true);
        setKeyPreview('***' + key.slice(-4));
        setOpenaiKey('');
        setShowKeyInput(false);
      }
      setSuccess(L.successSave);
    } catch {
      setError(L.errorSave);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty && !window.confirm(L.unsavedConfirm)) return;
    onClose();
  };

  const handleCancelSubscription = async () => {
    setCancelling(true); setError(''); setSuccess(''); setShowCancelModal(false); setCancelConfirmText('');
    try {
      await axios.post(`${API}/organizations/my/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCancelAtPeriodEnd(true);
      setSuccess(L.successCancelSub);
    } catch {
      setError(L.errorCancelSub);
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/organizations/my/reactivate-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCancelAtPeriodEnd(false);
      setSuccess(L.successReactivate);
    } catch {
      setError(L.errorReactivate);
    } finally {
      setReactivating(false);
    }
  };

  const handleChangePlan = async (newPlan: string) => {
    setChangingPlan(true); setError(''); setSuccess(''); setPendingPlan(null);
    try {
      await axios.post(`${API}/organizations/my/change-plan`, { plan: newPlan }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlan(newPlan);
      const dbL = dbPlanLimits[newPlan];
      const catalog = PLAN_CATALOG.find(p => p.id === newPlan);
      setMaxProjects((dbL ? dbL.maxProjects : catalog?.maxProjects) ?? null);
      setMaxUsers((dbL ? dbL.maxUsers : catalog?.maxUsers) ?? null);
      setShowPlanChange(false);
      setSuccess(L.successChangePlan);
      onPlanChanged?.(newPlan);
    } catch {
      setError(L.errorChangePlan);
    } finally {
      setChangingPlan(false);
    }
  };

  const handleTrialUpgrade = async (planId: string) => {
    setTrialCheckoutLoading(planId);
    try {
      const res = await axios.post(`${API}/payments/renewal-checkout`, { plan: planId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch {
      setError(L.errorCheckout);
    } finally {
      setTrialCheckoutLoading(null);
    }
  };

  const usageBarColor = (count: number, max: number | null) => {
    if (max === null) return '#10b981';
    const ratio = count / max;
    if (ratio >= 1) return '#ef4444';
    if (ratio >= 0.8) return '#f59e0b';
    return '#4f46e5';
  };

  const usagePct = (count: number, max: number | null) => {
    if (max === null) return 100;
    return Math.min((count / max) * 100, 100);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>{L.title}</h2>
            <p style={styles.modalSubtitle}>{L.subtitle}</p>
          </div>
          <button style={styles.closeBtn} onClick={handleClose}>✕</button>
        </div>

        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>{L.loading}</p>
        ) : (
          <>
            <div style={styles.columns}>
            <div style={styles.column}>
            {/* Όνομα οργανισμού */}
            <h3 style={styles.sectionTitle}>{L.orgNameSection}</h3>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              value={orgName}
              maxLength={100}
              onChange={e => { setOrgName(e.target.value); setSuccess(''); }}
              placeholder={L.orgNamePlaceholder}
            />

            <div style={styles.divider} />

            {/* AI Ρυθμίσεις */}
            <h3 style={styles.sectionTitle}>{L.aiSection}</h3>
            <p style={styles.hint}>{L.aiHint}</p>

            {/* API Key — provider auto-detected from prefix */}
            <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {L.apiKeyLabel}
              {' '}
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: activeProvider === 'anthropic' ? '#ede9fe' : '#e0f2fe',
                color: activeProvider === 'anthropic' ? '#7c3aed' : '#0369a1',
              }}>
                {activeProvider === 'anthropic' ? 'Anthropic Claude' : 'OpenAI'}
              </span>
            </p>
            {keySet && !showKeyInput ? (
              <div style={styles.keyBox}>
                <div style={styles.keyRow}>
                  <span style={styles.keyIcon}>🔑</span>
                  <span style={styles.keyPreview}>{keyPreview}</span>
                  <span style={styles.keyBadge}>{L.keyActive}</span>
                  <button style={styles.changeKeyBtn} onClick={() => { setShowKeyInput(true); setSuccess(''); }}>
                    {L.changeKey}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <input
                  style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                  type="password"
                  value={openaiKey}
                  onChange={e => handleKeyChange(e.target.value)}
                  placeholder={activeProvider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
                  autoComplete="off"
                />
                {!keySet && (
                  <p style={styles.noKeyWarning}>{L.noKeyWarning}</p>
                )}
              </>
            )}

            {/* Model selector — filtered by detected provider */}
            <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 16, marginBottom: 6 }}>{L.aiModelLabel}</p>
            <select
              style={{ ...styles.input, cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}
              value={aiModel}
              onChange={e => { setAiModel(e.target.value); setSuccess(''); }}
            >
              {visibleModels.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            {(() => {
              const selected = visibleModels.find(m => m.id === aiModel);
              if (!selected) return null;
              const desc = L.modelDescriptions[selected.id] ?? selected.description;
              if (!desc) return null;
              const isWarning = desc.startsWith('⚠️');
              return (
                <p style={{ fontSize: 12, margin: '6px 0 0', color: isWarning ? '#92400e' : '#6b7280', backgroundColor: isWarning ? '#fffbeb' : '#f9fafb', border: `1px solid ${isWarning ? '#fde68a' : '#e5e7eb'}`, borderRadius: 6, padding: '6px 10px', lineHeight: 1.5 }}>
                  {desc}
                </p>
              );
            })()}
            </div>

            <div style={styles.column}>
            {/* Συνδρομή */}
            <h3 style={styles.sectionTitle}>{L.subscriptionSection}</h3>
            {hasSubscription ? (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>⚡</span>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>{L.planLabel} {plan}</span>
                  </div>
                  <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{L.activeBadge}</span>
                </div>

                {/* Body */}
                <div style={{ background: '#fafafa', padding: '14px 20px' }}>
                  {/* Next payment */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>{L.nextRenewal}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                      {nextPaymentDate
                        ? new Date(nextPaymentDate).toLocaleDateString(language === 'en' ? 'en-US' : 'el-GR', { day: 'numeric', month: 'long', year: 'numeric' })
                        : '—'}
                    </span>
                  </div>

                  {/* Usage bars */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {/* Projects */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{L.projectsLabel}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                          {projectCount} / {maxProjects ?? '∞'}
                        </span>
                      </div>
                      <div style={{ height: 6, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${usagePct(projectCount, maxProjects)}%`,
                          backgroundColor: usageBarColor(projectCount, maxProjects),
                          borderRadius: 4,
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                    {/* Users */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{L.usersLabel}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                          {userCount} / {maxUsers ?? '∞'}
                        </span>
                      </div>
                      <div style={{ height: 6, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${usagePct(userCount, maxUsers)}%`,
                          backgroundColor: usageBarColor(userCount, maxUsers),
                          borderRadius: 4,
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  </div>

                  {/* Plan change toggle */}
                  <button
                    style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    onClick={() => setShowPlanChange(v => !v)}
                  >
                    {showPlanChange ? L.hidePlans : L.showPlans}
                  </button>

                  {/* Plan cards */}
                  {showPlanChange && (
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>{L.planChangeNote}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        {PLAN_CATALOG.map(p => {
                          const isCurrent = p.id === plan;
                          const livePrice = stripePrices[p.id] != null ? stripePrices[p.id]! : parseInt(p.priceNumber);
                          const dbL = dbPlanLimits[p.id];
                          const limitsText = L.limitsUnit(dbL ? dbL.maxProjects : p.maxProjects, dbL ? dbL.maxUsers : p.maxUsers);
                          return (
                            <div
                              key={p.id}
                              style={{
                                border: isCurrent ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                                borderRadius: 12,
                                padding: '16px 12px',
                                background: isCurrent ? 'rgba(79,70,229,0.03)' : '#fff',
                                textAlign: 'center',
                                position: 'relative',
                              }}
                            >
                              {p.highlighted && !isCurrent && (
                                <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4f46e5', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>{L.popular}</div>
                              )}
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{p.name}</div>
                              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, marginBottom: 4 }}>
                                <span style={{ fontSize: 20, fontWeight: 800, color: '#4f46e5' }}>€{livePrice}</span>
                                <span style={{ fontSize: 11, color: '#9ca3af' }}>{L.perMonth}</span>
                              </div>
                              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>{limitsText}</div>
                              {isCurrent ? (
                                <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 700 }}>{L.currentPlan}</span>
                              ) : (
                                <button
                                  onClick={() => setPendingPlan(p.id)}
                                  disabled={changingPlan}
                                  style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', width: '100%' }}
                                >
                                  {L.selectPlan}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{ background: '#fff', padding: '10px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 16 }}>
                  {cancelAtPeriodEnd ? (
                    <>
                      <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>{L.scheduledCancel}</span>
                      <button
                        onClick={handleReactivate}
                        disabled={reactivating}
                        style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                      >
                        {reactivating ? L.reactivating : L.reactivateBtn}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      disabled={cancelling}
                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                      {L.cancelBtn}
                    </button>
                  )}
                </div>
              </div>
            ) : plan === 'trial' ? (
              <div>
                <div style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.06), rgba(139,92,246,0.04))', border: '1px solid rgba(79,70,229,0.15)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, color: '#818cf8' }}>✦</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>{L.trialBanner}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {trialEndsAt ? (() => {
                        const days = Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000));
                        return days > 0
                          ? (language === 'en' ? `${days} day${days === 1 ? '' : 's'} remaining · ` : `Απομένουν ${days} ημέρες · `) + L.trialBannerSub
                          : L.trialBannerSub;
                      })() : L.trialBannerSub}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                  {PLAN_CATALOG.map(p => {
                    const livePrice = stripePrices[p.id] != null ? stripePrices[p.id]! : parseInt(p.priceNumber);
                    const dbL = dbPlanLimits[p.id];
                    const limitsText = L.limitsUnit(dbL ? dbL.maxProjects : p.maxProjects, dbL ? dbL.maxUsers : p.maxUsers);
                    return (
                      <div key={p.id} style={{ border: p.highlighted ? '2px solid #4f46e5' : '1px solid #e5e7eb', borderRadius: 12, padding: '20px 14px', textAlign: 'center', position: 'relative', background: p.highlighted ? 'rgba(79,70,229,0.03)' : '#fff' }}>
                        {p.highlighted && (
                          <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4f46e5', color: 'white', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' }}>{L.popular}</div>
                        )}
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{p.name}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, marginBottom: 4 }}>
                          <span style={{ fontSize: 26, fontWeight: 800, color: '#4f46e5' }}>€{livePrice}</span>
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>{L.perMonth}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>{limitsText}</div>
                        <button
                          onClick={() => handleTrialUpgrade(p.id)}
                          disabled={trialCheckoutLoading !== null}
                          style={{ width: '100%', padding: '10px', backgroundColor: p.highlighted ? '#4f46e5' : '#f3f4f6', color: p.highlighted ? '#fff' : '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {trialCheckoutLoading === p.id ? L.redirecting : L.selectPlan}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: '#9ca3af' }}>{L.noSubscription}</p>
            )}
            </div>
            </div>

            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}
          </>
        )}

        <div style={styles.modalButtons}>
          <button style={styles.closeBtnBottom} onClick={handleClose}>{L.close}</button>
          {!loading && (
            <button
              style={{ ...styles.saveBtn, ...(saving || !isDirty ? { opacity: 0.5, cursor: 'default' } : {}) }}
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? L.saving : L.save}
            </button>
          )}
        </div>
      </div>

      {/* Cancel subscription confirmation modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>
              ⚠️
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px', textAlign: 'center' }}>
              {L.cancelModalTitle}
            </h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px', textAlign: 'center' }}>
              {L.cancelModalDesc1}
            </p>
            <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 20px', textAlign: 'center' }}>
              {L.cancelModalDesc2}
            </p>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 8px', fontWeight: 600 }}>
              {L.cancelModalConfirmLabel} <span style={{ color: '#ef4444' }}>{L.cancelConfirmWord}</span> {L.cancelModalConfirmSuffix}
            </p>
            <input
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${cancelConfirmText === L.cancelConfirmWord ? '#ef4444' : '#ddd'}`, fontSize: '14px', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' as const, marginBottom: 20 }}
              value={cancelConfirmText}
              onChange={e => setCancelConfirmText(e.target.value)}
              placeholder={L.cancelConfirmWord}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setShowCancelModal(false); setCancelConfirmText(''); }}
                style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'transparent', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                {L.backBtn}
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelConfirmText !== L.cancelConfirmWord || cancelling}
                style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 8, backgroundColor: cancelConfirmText === L.cancelConfirmWord ? '#ef4444' : '#f3f4f6', color: cancelConfirmText === L.cancelConfirmWord ? '#fff' : '#9ca3af', fontSize: 14, fontWeight: 600, cursor: cancelConfirmText === L.cancelConfirmWord ? 'pointer' : 'not-allowed' }}
              >
                {cancelling ? L.cancellingBtn : L.confirmBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm plan change modal */}
      {pendingPlan && (() => {
        const p = PLAN_CATALOG.find(x => x.id === pendingPlan)!;
        const livePrice = stripePrices[p.id] != null ? stripePrices[p.id]! : parseInt(p.priceNumber);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>
                ⚡
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px' }}>
                {L.planChangeModalTitle(p.name)}
              </h3>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px' }}>
                €{livePrice}{L.perMonth} · {L.limitsUnit(dbPlanLimits[p.id] ? dbPlanLimits[p.id].maxProjects : p.maxProjects, dbPlanLimits[p.id] ? dbPlanLimits[p.id].maxUsers : p.maxUsers)}
              </p>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>
                {L.planChangeModalNote}
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setPendingPlan(null)}
                  style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'transparent', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  {L.cancelPlanBtn}
                </button>
                <button
                  onClick={() => handleChangePlan(pendingPlan)}
                  disabled={changingPlan}
                  style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 8, backgroundColor: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  {changingPlan ? L.changingBtn : L.confirmBtn}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '920px', maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  modalSubtitle: { fontSize: '12px', color: '#9ca3af', margin: '3px 0 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af', padding: '4px' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '8px', marginTop: 0 },
  hint: { fontSize: '12px', color: '#6b7280', marginBottom: '8px', marginTop: '-4px' },
  divider: { borderTop: '1px solid #e5e7eb', margin: '16px 0' },
  columns: { display: 'flex', gap: '28px', alignItems: 'flex-start' },
  column: { flex: 1, minWidth: 0 },
  input: { flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', outline: 'none', fontFamily: 'inherit' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  keyBox: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', marginBottom: '4px' },
  keyRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  keyIcon: { fontSize: '16px' },
  keyPreview: { fontSize: '14px', fontFamily: 'monospace', color: '#374151', flex: 1 },
  keyBadge: { backgroundColor: '#dcfce7', color: '#16a34a', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  changeKeyBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0 },
  noKeyWarning: { fontSize: '13px', color: '#d97706', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', marginTop: '8px' },
  error: { color: '#ef4444', fontSize: '13px', marginTop: '12px' },
  success: { color: '#10b981', fontSize: '13px', marginTop: '12px' },
  modalButtons: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' },
  closeBtnBottom: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
};

export default OrganizationSettings;
